import React, { useState } from 'react';

const KnowledgeBaseDashboard = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('faqs');
  const [faqs, setFaqs] = useState([]);
  const [unknownQuestions, setUnknownQuestions] = useState([]);
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
  const [editingFaq, setEditingFaq] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showExportModal, setShowExportModal] = useState(false);

  const [config, setConfig] = useState({
    companyInfo: '',
    allowedServices: '',
    contactEmail: '',
    autoEmailCollect: true,
    followupEnabled: true,
    followupDelay: 24,
    enableAttachments: true,
    enableAudio: true,
    maxFileSize: 10,
    allowedFileTypes: 'pdf,doc,docx,txt,jpg,jpeg,png'
  });

  const addFaq = () => {
    if (newFaq.question.trim() && newFaq.answer.trim()) {
      const faq = {
        id: `faq_${Date.now()}`,
        question: newFaq.question.trim(),
        answer: newFaq.answer.trim(),
        createdBy: currentUser?.firstName || 'User',
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      
      setFaqs(prev => [...prev, faq]);
      setNewFaq({ question: '', answer: '' });
    }
  };

  const updateFaq = (id, updates) => {
    setFaqs(prev => prev.map(faq => 
      faq.id === id 
        ? { ...faq, ...updates, updatedAt: Date.now() }
        : faq
    ));
    setEditingFaq(null);
  };

  const deleteFaq = (id) => {
    if (window.confirm('Are you sure you want to delete this FAQ?')) {
      setFaqs(prev => prev.filter(faq => faq.id !== id));
    }
  };

  const convertUnknownToFaq = (question) => {
    setNewFaq({ question: question, answer: '' });
    setActiveTab('faqs');
  };

  const clearUnknownQuestions = () => {
    if (window.confirm('Are you sure you want to clear all unknown questions?')) {
      setUnknownQuestions([]);
    }
  };

  const getFilteredFaqs = () => {
    let filtered = faqs.filter(faq => 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    );

    switch (sortBy) {
      case 'newest':
        return filtered.sort((a, b) => b.createdAt - a.createdAt);
      case 'oldest':
        return filtered.sort((a, b) => a.createdAt - b.createdAt);
      case 'alphabetical':
        return filtered.sort((a, b) => a.question.localeCompare(b.question));
      default:
        return filtered;
    }
  };

  const getSortedUnknownQuestions = () => {
    return [...unknownQuestions].sort((a, b) => (b.count || 1) - (a.count || 1));
  };

  const exportKnowledgeBase = () => {
    const exportData = {
      export_date: new Date().toISOString(),
      version: '1.0',
      faqs: faqs,
      config: config,
      unknown_questions: unknownQuestions
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `defindly-knowledge-base-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    setShowExportModal(false);
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target.result);
        
        if (importData.faqs && Array.isArray(importData.faqs)) {
          setFaqs(prev => [...prev, ...importData.faqs]);
        }
        
        if (importData.config) {
          setConfig(prev => ({ ...prev, ...importData.config }));
        }
        
        alert('Knowledge base imported successfully!');
      } catch (error) {
        alert('Invalid file format. Please upload a valid JSON export file.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="page-title">Knowledge Base Management</h1>
          <p className="page-subtitle">Manage FAQs, analyze questions, and configure your AI assistant</p>
        </div>

        <div className="tab-nav">
          {[
            { id: 'faqs', label: 'FAQ Manager', count: faqs.length },
            { id: 'unknown', label: 'Unknown Questions', count: unknownQuestions.length },
            { id: 'settings', label: 'Company Information' },
            { id: 'import-export', label: 'Import/Export' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            >
              {tab.label} {tab.count !== undefined && `(${tab.count})`}
            </button>
          ))}
        </div>

        {activeTab === 'faqs' && (
          <div>
            <div className="flex gap-4 mb-6">
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input flex-1"
              />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="form-select"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="alphabetical">A-Z</option>
              </select>
            </div>

            <div className="card p-6 mb-6">
              <h3 className="section-title">Add New FAQ</h3>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Question"
                  value={newFaq.question}
                  onChange={(e) => setNewFaq(prev => ({ ...prev, question: e.target.value }))}
                  className="form-input"
                />
                <textarea
                  placeholder="Answer"
                  value={newFaq.answer}
                  onChange={(e) => setNewFaq(prev => ({ ...prev, answer: e.target.value }))}
                  rows={4}
                  className="form-textarea"
                />
                <button
                  className="btn-primary"
                  onClick={addFaq}
                  disabled={!newFaq.question.trim() || !newFaq.answer.trim()}
                >
                  Add FAQ
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {getFilteredFaqs().map((faq) => (
                <div key={faq.id} className="faq-card">
                  {editingFaq === faq.id ? (
                    <div className="space-y-4">
                      <input
                        type="text"
                        value={faq.question}
                        onChange={(e) => setFaqs(prev => prev.map(f => 
                          f.id === faq.id ? { ...f, question: e.target.value } : f
                        ))}
                        className="form-input"
                      />
                      <textarea
                        value={faq.answer}
                        onChange={(e) => setFaqs(prev => prev.map(f => 
                          f.id === faq.id ? { ...f, answer: e.target.value } : f
                        ))}
                        rows={4}
                        className="form-textarea"
                      />
                      <div className="flex gap-3">
                        <button
                          className="btn-primary"
                          onClick={() => updateFaq(faq.id, { question: faq.question, answer: faq.answer })}
                        >
                          Save
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => setEditingFaq(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="faq-question">{faq.question}</h4>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingFaq(faq.id)}
                            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 transition-all"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteFaq(faq.id)}
                            className="px-3 py-1.5 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg text-xs font-medium text-red-600 transition-all"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <p className="faq-answer">{faq.answer}</p>
                      <div className="faq-meta">
                        Created by {faq.createdBy} on {new Date(faq.createdAt).toLocaleDateString()}
                        {faq.updatedAt !== faq.createdAt && ` • Updated ${new Date(faq.updatedAt).toLocaleDateString()}`}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {getFilteredFaqs().length === 0 && (
                <div className="text-center py-12 card">
                  <p className="text-gray-500">
                    {searchTerm ? 'No FAQs match your search.' : 'No FAQs yet. Add your first FAQ above!'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'unknown' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Questions Users Asked That Couldn't Be Answered</h3>
              {unknownQuestions.length > 0 && (
                <button className="btn-danger" onClick={clearUnknownQuestions}>
                  Clear All
                </button>
              )}
            </div>

            {getSortedUnknownQuestions().length === 0 ? (
              <div className="text-center py-12 card">
                <p className="text-gray-500">
                  No unknown questions yet. This is good - your knowledge base is comprehensive!
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {getSortedUnknownQuestions().map((q) => (
                  <div key={q.id} className="card p-5">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          "{q.question}"
                        </h4>
                        <div className="text-xs text-gray-500">
                          Asked {q.count || 1} time{(q.count || 1) > 1 ? 's' : ''} • 
                          Last: {new Date(q.lastAsked).toLocaleDateString()} • 
                          User: {q.userInfo || 'Anonymous'}
                        </div>
                      </div>
                      <button className="btn-primary" onClick={() => convertUnknownToFaq(q.question)}>
                        Convert to FAQ
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Company Information</h3>
            <div className="card p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Tell us about your company</h4>
              <p className="text-gray-600 text-sm mb-4">
                Write as much detail as possible about your company, products, services, values, and unique offerings. 
                The AI assistant learns from this information to provide accurate and relevant responses to your customers. 
                Include information about your mission, history, team, pricing, policies, and anything else that would help 
                the assistant understand your business better.
              </p>
              <textarea
                value={config.companyInfo}
                onChange={(e) => setConfig(prev => ({ ...prev, companyInfo: e.target.value }))}
                placeholder="Example: We are a software company founded in 2020, specializing in AI-powered customer service solutions. Our main products include chatbots, knowledge base systems, and analytics tools. We serve small to medium businesses across retail, healthcare, and finance sectors. Our pricing starts at $49/month for the basic plan..."
                rows={12}
                className="form-textarea"
              />
            </div>
          </div>
        )}

        {activeTab === 'import-export' && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Import/Export Knowledge Base</h3>
            <div className="grid gap-6">
              <div className="card p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Export Knowledge Base</h4>
                <p className="text-gray-600 text-sm mb-4">Download all your FAQs and settings as a JSON file</p>
                <button className="btn-primary" onClick={() => setShowExportModal(true)}>
                  Download Knowledge Base
                </button>
              </div>

              <div className="card p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Import Knowledge Base</h4>
                <p className="text-gray-600 text-sm mb-4">Upload a JSON file to restore or merge FAQs</p>
                <label htmlFor="file-upload" className="btn-primary cursor-pointer inline-flex items-center justify-center">
                  Upload JSON File
                </label>
                <input
                  id="file-upload"
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </div>
        )}

        {showExportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Export Knowledge Base</h3>
              <p className="text-gray-600 mb-6">
                This will download a JSON file containing all your FAQs and settings.
              </p>
              <div className="flex gap-3 justify-end">
                <button className="btn-secondary" onClick={() => setShowExportModal(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={exportKnowledgeBase}>
                  Download
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBaseDashboard;